// Interpreta o CRLV (e outros documentos do veículo) e extrai os dados do cadastro de veículo:
// placa, RENAVAM, chassi, motor, marca/modelo, cor e ano (fabricação/modelo). No CRLV também lê o
// proprietário (nome e CPF/CNPJ). Funções puras, sem I/O.
//
// Duas fontes:
//  - itens de texto com posição (PDF digital, como o CRLV-e): cada rótulo fica logo acima do seu valor,
//    então lemos pela posição. Não precisa de OCR e não erra letra.
//  - texto corrido de OCR (foto ou PDF escaneado): melhor esforço, procurando pelos rótulos.

import { documentoValido, normalizarDocumento } from './documentoFiscal';

export const placaValida = (p) => /^[A-Z]{3}\d[A-Z0-9]\d{2}$/.test(p || '');

// VIN tem 17 caracteres e nunca usa as letras I, O e Q
export const chassiValido = (c) => /^[A-HJ-NPR-Z0-9]{17}$/.test(c || '');

// RENAVAM: 9 a 11 dígitos; o último é o dígito verificador (módulo 11, pesos 3298765432)
export function renavamValido(valor) {
  const r = (valor || '').replace(/\D/g, '');
  if (r.length < 9 || r.length > 11) return false;
  const completo = r.padStart(11, '0');
  const pesos = [3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const soma = pesos.reduce((acc, peso, i) => acc + Number(completo[i]) * peso, 0);
  const dv = (soma * 10) % 11;
  return (dv === 10 ? 0 : dv) === Number(completo[10]);
}

// Padrão de placa gravado no sistema: com hífen (AAA-0A00). A busca por placa no backend é por igualdade exata,
// então uma placa gravada sem hífen não seria encontrada depois pela tela de Clientes.
export const formatarPlaca = (p) => {
  const s = (p || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return s.length === 7 ? `${s.slice(0, 3)}-${s.slice(3)}` : s;
};

const soAlfanumerico = (t) => (t || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const anoValido = (a) => /^(19|20)\d{2}$/.test(a || '');

// ─── Placa: padrão antigo ↔ Mercosul ─────────────────────────────────────────
//
// Ao trocar para o padrão Mercosul, a placa não é sorteada de novo: só o 5º caractere (o 2º dígito) vira
// letra, pela ordem 0=A ... 9=J. ABC1234 vira ABC1C34, e a conversão é reversível.
// Importa porque o CRLV traz a placa atual, mas o veículo pode estar no cadastro com a placa antiga
// (ou o contrário) — sem converter, o sistema criaria um veículo duplicado.

const LETRAS_MERCOSUL = 'ABCDEFGHIJ';

// Placa antiga (AAA0000) na forma Mercosul. Devolve '' se já for Mercosul ou não for placa.
export function placaParaMercosul(placa) {
  const p = soAlfanumerico(placa);
  if (!/^[A-Z]{3}\d{4}$/.test(p)) return '';
  return `${p.slice(0, 4)}${LETRAS_MERCOSUL[Number(p[4])]}${p.slice(5)}`;
}

// Caminho inverso. Só placas convertidas voltam: as emitidas já no padrão novo podem ter K–Z na 5ª
// posição, e essas nunca tiveram forma antiga.
export function placaParaPadraoAntigo(placa) {
  const p = soAlfanumerico(placa);
  if (!/^[A-Z]{3}\d[A-J]\d{2}$/.test(p)) return '';
  return `${p.slice(0, 4)}${LETRAS_MERCOSUL.indexOf(p[4])}${p.slice(5)}`;
}

// Todas as formas sob as quais esta placa pode estar gravada: como veio, com e sem hífen, e a
// convertida (também com e sem hífen). Para procurar no cadastro sem depender do padrão usado no dia.
//
// A placa exatamente como foi digitada vem primeiro e sempre. Há cadastros com placa fora do padrão
// ("LIC- DIG", "I8H-F175", erro de digitação antigo): normalizar essas para alfanumérico geraria uma
// forma que não existe no banco, e o veículo deixaria de ser encontrado — virando duplicata.
export function variantesDePlaca(placa) {
  const comoVeio = (placa || '').trim().toUpperCase();
  const p = soAlfanumerico(comoVeio);
  if (!comoVeio) return [];
  const formas = [comoVeio];
  const adicionar = (v) => { if (v && !formas.includes(v)) formas.push(v); };
  for (const forma of [p, placaParaMercosul(p), placaParaPadraoAntigo(p)]) {
    if (!forma) continue;
    adicionar(forma);
    adicionar(formatarPlaca(forma));
  }
  return formas;
}

// O número do motor é o único campo em que os separadores impressos são preservados (JC25E-Y033482): os
// documentos de motor declaram esse número e precisam reproduzi-lo como está no CRLV. Saem os asteriscos
// (o CRLV usa * como preenchimento), os espaços e qualquer separador que sobre nas pontas.
const normalizarMotor = (t) => (t || '').toUpperCase().replace(/[^A-Z0-9./-]/g, '').replace(/^[./-]+|[./-]+$/g, '');

// ─── Leitura por posição (PDF digital) ────────────────────────────────────────

const ROTULOS = {
  placa: /^PLACA$/i,
  renavam: /^C[ÓO]DIGO\s+RENAVAM$/i,
  chassi: /^CHASSI$/i,
  marcaModelo: /^MARCA\s*\/\s*MODELO/i,
  cor: /^COR\s+PREDOMINANTE$/i,
  anoFabricacao: /^ANO\s+FABRICA[ÇC][ÃA]O$/i,
  anoModelo: /^ANO\s+MODELO$/i,
  motor: /^(N[ºO°.]*\s*(D[OE]\s+)?)?MOTOR$/i,
  placaAnterior: /^PLACA\s+ANTERIOR/i,
  // Ano do licenciamento a que este CRLV se refere. É o que diz se um documento substitui o anterior.
  exercicio: /^EXERC[ÍI]CIO$/i,
  // Proprietário. No CRLV-e o rótulo do documento vem com espaços em volta da barra ("CPF / CNPJ")
  nomeProprietario: /^NOME$/i,
  documentoProprietario: /^CPF\s*\/\s*CNPJ$/i,
};

// Só o CRLV tem "o proprietário". O ATPV-e e o CRV trazem vendedor E comprador, e escolher o nome errado
// vincularia o veículo ao cliente errado — por isso o proprietário só é lido quando o documento se
// identifica como CRLV. Nos outros, quem preenche é o usuário.
const MARCA_CRLV = /CERTIFICADO\s+DE\s+REGISTRO\s+E\s+LICENCIAMENTO\s+DE\s+VE[ÍI]CULO/i;

const TOLERANCIA_X = 6; // o valor começa na mesma coluna do rótulo
const DISTANCIA_MAXIMA_Y = 32; // e fica logo abaixo dele
const MESMA_LINHA = 2;
const LACUNA_MAXIMA = 6; // pedaços do mesmo valor estão quase colados; colunas vizinhas ficam bem mais longe

// Valor impresso logo abaixo do rótulo (fonte maior que a do rótulo). No PDF, y cresce para cima.
function valorAbaixoDoRotulo(itens, regexRotulo) {
  const rotulo = itens.find((i) => regexRotulo.test(i.str.trim()));
  if (!rotulo) return '';

  const primeiro = itens
    .filter((i) => i !== rotulo && i.str.trim()
      && i.h >= rotulo.h + 2
      && Math.abs(i.x - rotulo.x) <= TOLERANCIA_X
      && rotulo.y - i.y > 0 && rotulo.y - i.y <= DISTANCIA_MAXIMA_Y)
    .sort((a, b) => (rotulo.y - a.y) - (rotulo.y - b.y))[0];
  if (!primeiro) return '';

  // O valor pode vir em pedaços na mesma linha: junta os que estão colados
  const mesmaLinha = itens
    .filter((i) => i.str.trim() && Math.abs(i.y - primeiro.y) <= MESMA_LINHA && i.x >= primeiro.x)
    .sort((a, b) => a.x - b.x);
  let texto = '';
  let fim = null;
  for (const item of mesmaLinha) {
    if (fim !== null && item.x - fim > LACUNA_MAXIMA) break;
    texto += `${texto ? ' ' : ''}${item.str.trim()}`;
    fim = item.x + item.w;
  }
  return texto;
}

// Nome de pessoa: duas palavras ou mais, só letras (com acento), espaços e os separadores de nome
// composto. Um campo mascarado do CRLV ("***") ou uma linha de rótulo não passam.
const NOME_PROPRIETARIO = /^[A-ZÀ-Ý][A-ZÀ-Ý'.-]*(\s+[A-ZÀ-Ý'.-]+)+$/;

// O proprietário só sai do CRLV, e só quando nome e documento estão os dois legíveis: meio proprietário
// não serve para achar nem para cadastrar cliente, e um CPF sem dono levaria o veículo para o lugar errado.
// Quando havia algo escrito nesses campos mas não passou na validação, entra a dúvida 'proprietario':
// é o caso em que vale o usuário conferir o documento, diferente de o campo simplesmente não existir ali.
function montarProprietario(bruto, duvidas) {
  if (!bruto.ehCrlv) return undefined;
  const nome = (bruto.nomeProprietario || '').replace(/\s+/g, ' ').trim().toUpperCase();
  const cpfCnpj = normalizarDocumento(bruto.documentoProprietario);
  if (!NOME_PROPRIETARIO.test(nome) || !documentoValido(cpfCnpj)) {
    if (nome || cpfCnpj) duvidas.push('proprietario');
    return undefined;
  }
  return { nome, cpfCnpj };
}

// Formato comum às duas fontes: valida, normaliza e separa o que parece errado
function montarResultado(bruto) {
  const dados = {};
  const duvidas = [];

  const placa = soAlfanumerico(bruto.placa);
  if (placaValida(placa)) dados.placa = placa; else if (placa) duvidas.push('placa');

  const renavam = (bruto.renavam || '').replace(/\D/g, '');
  if (renavam.length >= 9 && renavam.length <= 11) {
    dados.renavam = renavam;
    if (!renavamValido(renavam)) duvidas.push('renavam');
  }

  const chassi = soAlfanumerico(bruto.chassi);
  if (chassiValido(chassi)) dados.chassi = chassi; else if (chassi.length >= 10) { dados.chassi = chassi; duvidas.push('chassi'); }

  const fabricacao = (bruto.anoFabricacao || '').replace(/\D/g, '');
  const modelo = (bruto.anoModelo || '').replace(/\D/g, '');
  if (anoValido(fabricacao) && anoValido(modelo)) dados.ano = `${fabricacao}/${modelo}`;
  else if (anoValido(modelo) || anoValido(fabricacao)) dados.ano = modelo || fabricacao;

  const marcaModelo = (bruto.marcaModelo || '').replace(/\s+/g, ' ').trim().toUpperCase();
  if (marcaModelo.length >= 3) dados.marcaModelo = marcaModelo;

  const cor = (bruto.cor || '').replace(/\s+/g, ' ').trim().toUpperCase();
  if (cor.length >= 3 && /^[A-ZÀ-Ý ]+$/.test(cor)) dados.cor = cor;

  // Motor: sem padrão fixo (varia por fabricante); exige ao menos um número e não pode ser o chassi/placa lidos.
  // A conferência ignora os separadores, para que "9C2-JC25..." não escape de ser reconhecido como o chassi.
  const motor = normalizarMotor(bruto.motor);
  const motorComparavel = soAlfanumerico(motor);
  if (motorComparavel.length >= 5 && motorComparavel.length <= 25 && /\d/.test(motorComparavel)
      && motorComparavel !== dados.chassi && motorComparavel !== dados.placa) {
    dados.motor = motor;
  }

  // A placa anterior é o caminho mais curto para achar no cadastro um veículo que ainda está com a placa
  // velha. No CRLV ela vem como "AAA0000/UF" e, quando não existe, mascarada ("*******/**"). Nunca
  // substitui a placa atual: é só mais um lugar onde procurar.
  const placaAnterior = soAlfanumerico((bruto.placaAnterior || '').split('/')[0]);
  if (placaValida(placaAnterior) && placaAnterior !== dados.placa) dados.placaAnterior = placaAnterior;

  // Quem lê precisa saber se o documento é mesmo um CRLV: a importação em lote só aceita CRLV-e, e
  // "não é CRLV" é um aviso bem diferente de "não achei o proprietário".
  if (bruto.ehCrlv) dados.ehCrlv = true;

  // Exercício: o ano do licenciamento a que este CRLV se refere. É o que permite saber que o
  // documento novo substitui o do ano anterior, em vez de se acumular ao lado dele.
  const exercicio = (bruto.exercicio || '').replace(/\D/g, '');
  if (anoValido(exercicio)) dados.exercicio = Number(exercicio);

  const proprietario = montarProprietario(bruto, duvidas);
  if (proprietario) dados.proprietario = proprietario;

  return { dados, duvidas };
}

// itens: [{ str, x, y, w, h }] — texto do PDF com a posição de cada pedaço
export function extrairVeiculoDeItens(itens) {
  const lista = itens || [];
  return montarResultado({
    placa: valorAbaixoDoRotulo(lista, ROTULOS.placa),
    renavam: valorAbaixoDoRotulo(lista, ROTULOS.renavam),
    chassi: valorAbaixoDoRotulo(lista, ROTULOS.chassi),
    marcaModelo: valorAbaixoDoRotulo(lista, ROTULOS.marcaModelo),
    cor: valorAbaixoDoRotulo(lista, ROTULOS.cor),
    anoFabricacao: valorAbaixoDoRotulo(lista, ROTULOS.anoFabricacao),
    anoModelo: valorAbaixoDoRotulo(lista, ROTULOS.anoModelo),
    motor: valorAbaixoDoRotulo(lista, ROTULOS.motor),
    placaAnterior: valorAbaixoDoRotulo(lista, ROTULOS.placaAnterior),
    exercicio: valorAbaixoDoRotulo(lista, ROTULOS.exercicio),
    ehCrlv: lista.some((i) => MARCA_CRLV.test(i.str || '')),
    nomeProprietario: valorAbaixoDoRotulo(lista, ROTULOS.nomeProprietario),
    documentoProprietario: valorAbaixoDoRotulo(lista, ROTULOS.documentoProprietario),
  });
}

// ─── Leitura de texto corrido (OCR de foto ou PDF escaneado) ───────────────────

// Procura o valor no mesmo trecho do rótulo (depois dele) ou nas linhas seguintes
// `ignorar`: valores que na verdade são outro rótulo da mesma linha (ex.: COMBUSTÍVEL ao lado de COR PREDOMINANTE)
function depoisDoRotulo(linhas, regexRotulo, regexValor, linhasAFrente = 2, ignorar = null) {
  for (let i = 0; i < linhas.length; i++) {
    const achado = linhas[i].match(regexRotulo);
    if (!achado) continue;
    const resto = linhas[i].slice(achado.index + achado[0].length);
    for (const trecho of [resto, ...linhas.slice(i + 1, i + 1 + linhasAFrente)]) {
      const valor = trecho.match(regexValor);
      if (valor && !(ignorar && ignorar.test(valor[0]))) return valor[0];
    }
  }
  return '';
}

export function extrairVeiculoDeTexto(texto) {
  const original = texto || '';
  const linhas = original.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  // O chassi é a única sequência de 17 caracteres válidos do documento
  const chassi = (original.toUpperCase().match(/\b[A-HJ-NPR-Z0-9]{17}\b/g) || []).find((c) => /\d/.test(c) && /[A-Z]/.test(c)) || '';

  const anos = (linha) => (linha.match(/\b(19|20)\d{2}\b/g) || []);
  let anoFabricacao = '';
  let anoModelo = '';
  for (let i = 0; i < linhas.length && !anoFabricacao; i++) {
    if (/ANO\s+FABRICA[ÇC][ÃA]O/i.test(linhas[i])) {
      const encontrados = [...anos(linhas[i]), ...anos(linhas[i + 1] || '')];
      [anoFabricacao, anoModelo] = [encontrados[0] || '', encontrados[1] || ''];
    }
  }

  return montarResultado({
    placa: depoisDoRotulo(linhas, /\bPLACA\b(?!\s*ANTERIOR)/i, /\b[A-Z]{3}[-\s]?\d[A-Z0-9]\d{2}\b/i),
    renavam: depoisDoRotulo(linhas, /RENAVAM/i, /\b\d{9,11}\b/),
    chassi,
    marcaModelo: depoisDoRotulo(linhas, /MARCA\s*\/\s*MODELO(\s*\/\s*VERS[ÃA]O)?/i, /[A-Z0-9][A-Z0-9/. -]{2,}/i, 1),
    // só a primeira palavra: no OCR a coluna do combustível costuma vir colada na cor
    cor: depoisDoRotulo(linhas, /COR\s+PREDOMINANTE/i, /[A-ZÀ-Ý]{3,}/, 1, /^(COMBUST[ÍI]VEL|PLACA|CHASSI|MOTOR)$/),
    anoFabricacao,
    anoModelo,
    // "MOTOR" como rótulo isolado (não pega "AUTOMOTORES"); o valor tem ao menos um número.
    // Ignora só-números de 9 a 14 dígitos: no OCR costumam ser RENAVAM/CPF/CNPJ da linha vizinha.
    motor: depoisDoRotulo(linhas, /\b(N[ºO°.]*\s*(D[OE]\s+)?)?MOTOR\b/i, /\b(?=[A-Z0-9./-]*\d)[A-Z0-9][A-Z0-9./-]{3,23}[A-Z0-9]\b/i, 1, /^[\d./-]{9,18}$/),
    // Proprietário: melhor esforço, mas montarProprietario só o aceita com o dígito verificador do
    // CPF/CNPJ correto — um número que o OCR errou não passa, e sem documento o nome sozinho é descartado.
    placaAnterior: depoisDoRotulo(linhas, /PLACA\s+ANTERIOR/i, /\b[A-Z]{3}[-\s]?\d[A-Z0-9]\d{2}\b/i, 1),
    exercicio: depoisDoRotulo(linhas, /EXERC[ÍI]CIO/i, /\b(19|20)\d{2}\b/, 1),
    ehCrlv: MARCA_CRLV.test(original),
    nomeProprietario: depoisDoRotulo(linhas, /\bNOME\b/i, /[A-ZÀ-Ý][A-ZÀ-Ý'.-]*(\s+[A-ZÀ-Ý'.-]+)+/, 1, /^(CPF|CNPJ|LOCAL|DATA|MARCA|MODELO|CHASSI|MOTOR)\b/i),
    documentoProprietario: depoisDoRotulo(linhas, /\bCPF\s*\/?\s*CNPJ\b/i, /\b[\d][\d./-]{9,16}[\d]\b/, 1),
  });
}
